import { SearchSideBarLayout } from 'components/SearchSideBar/SearchSidebarLayout';
import { FC } from 'react';
import { SearchKeyword } from 'components/SearchSideBar/SearchKeyword';
import { OverlaySearchArea } from 'components/SearchSideBar/OverlaySearchArea';
import { SearchButtonArea } from 'components/SearchSideBar/SearchButtonArea';
import { useShowOverlay } from 'components/SearchSideBar/hooks/useShowOverlay';
import { PickUpConditions } from 'components/SearchSideBar/PickUpCondition';
import { Details } from 'components/SearchSideBar/Details';
import { useCountUpdateAnimation } from 'hooks/useCountUpdateAnimation';
import { JobCountResponse } from '@doda/api-client/aspida/@types';
import { ModalOverlay } from 'components/ModalOverlay';
import { useDisclosure } from '@doda/common/src/hooks/useDisclosure';
import { InitialSideBarMasterDataType } from 'components/SearchSideBar/BasicInfo/types';
import { useSearchCondition } from 'components/SearchSideBar/hooks/useSearchCondition';
import { BasicInfo } from 'components/SearchSideBar/BasicInfo';
import { FeaturedSearchConditionQuery } from 'components/SearchSideBar/types';
import { createOccupationDisplayData } from 'components/OccupationSelectModal/logics/createOccupationDisplayData';
import {
  createDisplayArea,
  createDisplayCity,
} from 'components/LocationStationModal/LocationModal/logics/createLocationDisplayData';
import { createDisplayStation } from 'components/LocationStationModal/StationModal/logics/createStationDisplayData';
import { useStationModal } from 'components/LocationStationModal/StationModal/hooks/useStationModal';
import { createIndustryDisplayData } from 'components/IndustryModal/logics/createIndustryDisplayData';
import { useAppSelector } from 'redux/hooks';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { useEachModalJobCount } from 'hooks/useEachModalJobCount';
import dynamic from 'next/dynamic';
import { useSearch } from './hooks/useSearch';
import { NormalModalMasterData } from '../interface';

const OccupationModal = dynamic(
  () =>
    import('components/OccupationSelectModal').then((module) => ({
      default: module.OccupationModal,
    })),
  { ssr: false }
);
const LocationModal = dynamic(
  () =>
    import('components/LocationStationModal/LocationModal').then((module) => ({
      default: module.LocationModal,
    })),
  { ssr: false }
);
const StationModal = dynamic(
  () =>
    import('components/LocationStationModal/StationModal').then((module) => ({
      default: module.StationModal,
    })),
  { ssr: false }
);
const IndustryModal = dynamic(
  () =>
    import('components/IndustryModal').then((module) => ({
      default: module.IndustryModal,
    })),
  { ssr: false }
);

/**
 * Propsの定義
 */
interface Props {
  initialJobCountData: JobCountResponse;
  initialSideBarMasterData: InitialSideBarMasterDataType;
  modalMasterData: NormalModalMasterData['data'];
  featuredSearchCondition: FeaturedSearchConditionQuery;
}

export const NormalSearchSideBar: FC<Props> = ({
  initialJobCountData,
  initialSideBarMasterData,
  modalMasterData,
  featuredSearchCondition,
}) => {
  const { fid } = useAppSelector(appQueryParamsSelector);

  const {
    occupation,
    location,
    routeStation,
    industry,
    employmentStatus,
    income,
    includeSearchKeyword,
    excludeSearchKeyword,
    pickUpCondition,
    searchDetail,
    updateJobCount,
    totalCount,
    selectedCurrentCondition,
    modalTotalCount,
    updateModalJobCount,
    isConditionUpperLimitError,
  } = useSearchCondition({
    initialSideBarMasterData,
    initialJobCountData,
    featuredSearchCondition,
  });

  const {
    onClickSearchButton,
    onClickOccupationModalSearchButton,
    onClickLocationModalSearchButton,
    onClickRoteStationModalSearchButton,
    onClickIndustryModalSearchButton,
  } = useSearch(selectedCurrentCondition, fid);
  const { isOpacity, isInvisible, onChangeIsShowOverlay, observerRef } =
    useShowOverlay();
  const countAnimationRef = useCountUpdateAnimation(totalCount);

  const occupationModal = useDisclosure();
  const industryModal = useDisclosure();
  const locationModal = useDisclosure();

  const {
    occupationModalJobCount,
    locationAndStationModalJobCount,
    industryModalJobCount,
    getJobCountWithoutOccupation,
    getJobCountWithoutLocationAndStation,
    getJobCountWithoutIndustry,
  } = useEachModalJobCount(selectedCurrentCondition);

  const {
    stationModal,
    routeMaster,
    onOpenRouteModal,
    reloadRouteMaster,
    checkedStationState,
    checkedStationDispatch,
  } = useStationModal({
    selectedCurrentCondition,
    locationAndStationModalJobCount,
    areaMaster: modalMasterData.area,
  });

  return (
    <>
      <SearchSideBarLayout>
        <BasicInfo
          conditions={{
            occupation,
            location,
            routeStation,
            industry,
            employmentStatus,
            income,
          }}
          modalAction={{
            onOpenOccupationModal: occupationModal.onOpen,
            onOpenLocationModal: locationModal.onOpen,
            onOpenRouteModal,
            onOpenIndustryModal: industryModal.onOpen,
          }}
          getJobCountAtModalDisplay={{
            getJobCountWithoutOccupation,
            getJobCountWithoutLocationAndStation,
            getJobCountWithoutIndustry,
          }}
          updateJobCount={updateJobCount}
          onChangeIsShowOverlay={onChangeIsShowOverlay}
        />
        <PickUpConditions
          list={pickUpCondition.pickUpConditionDisplayList}
          updateIsCheckedIsNewArrival={
            pickUpCondition.updateIsCheckedIsNewArrival
          }
          updateIsCheckedIsNearDeadline={
            pickUpCondition.updateIsCheckedIsNearDeadline
          }
          updateIsCheckedHolidayAndWayOfWorkingList={
            searchDetail.holidayAndWayOfWorking
              .updateIsCheckedHolidayAndWayOfWorkingList
          }
          updateIsCheckedRequirementList={
            searchDetail.requirements.updateIsCheckedRequirementList
          }
          updateJobCount={updateJobCount}
          onChangeIsShowOverlay={onChangeIsShowOverlay}
        />
        <SearchKeyword
          include={includeSearchKeyword}
          exclude={excludeSearchKeyword}
          updateJobCount={updateJobCount}
          onChangeIsShowOverlay={onChangeIsShowOverlay}
        />
        <Details
          searchDetail={searchDetail}
          updateJobCount={updateJobCount}
          onChangeIsShowOverlay={onChangeIsShowOverlay}
        />
        <SearchButtonArea
          onClickSearchButton={() => onClickSearchButton()}
          observerRef={observerRef}
          countAnimationRef={countAnimationRef}
          validationFlags={{
            isIncomeRangeError: income.isIncomeRangeError,
            isConditionUpperLimitError,
          }}
        />
        <OverlaySearchArea
          totalCount={totalCount}
          isOpacity={isOpacity}
          isInvisible={isInvisible}
          onClickSearchButton={() => onClickSearchButton()}
          validationFlags={{
            isIncomeRangeError: income.isIncomeRangeError,
            isConditionUpperLimitError,
          }}
        />
      </SearchSideBarLayout>
      <ModalOverlay
        show={
          occupationModal.isOpen ||
          locationModal.isOpen ||
          stationModal.isOpen ||
          industryModal.isOpen
        }
        onClickOverlay={() => {
          occupationModal.onClose();
          locationModal.onClose();
          stationModal.onClose();
          industryModal.onClose();
        }}
      >
        <OccupationModal
          show={occupationModal.isOpen}
          onClickClose={occupationModal.onClose}
          onClickSearch={(checkedState) => {
            onClickOccupationModalSearchButton({
              searchJobTypeLId: checkedState.L,
              searchJobTypeMId: checkedState.M,
              searchJobTypeSId: checkedState.S,
            });
          }}
          onChangeSelectedOccupation={(checkedState) => {
            updateModalJobCount({
              searchJobTypeLId: checkedState.L,
              searchJobTypeMId: checkedState.M,
              searchJobTypeSId: checkedState.S,
            });
          }}
          displayOccupationData={createOccupationDisplayData(
            modalMasterData.occupation,
            occupationModalJobCount
          )}
          matchJobCount={modalTotalCount}
          submitButtonText="この条件で検索する"
          selectedOccupationIdList={{
            L: selectedCurrentCondition.searchJobTypeLId,
            M: selectedCurrentCondition.searchJobTypeMId,
            S: selectedCurrentCondition.searchJobTypeSId,
          }}
          validationFlags={{
            isIncomeRangeError: income.isIncomeRangeError,
            isConditionUpperLimitError,
          }}
        />
        <LocationModal
          show={locationModal.isOpen}
          onClickClose={locationModal.onClose}
          initialTotalCount={modalTotalCount}
          selectedLocationModalIdList={{
            checkedArea: selectedCurrentCondition.areaId,
            checkedPref: selectedCurrentCondition.prefectureId,
            checkedCity: selectedCurrentCondition.cityId,
            checkedWard: selectedCurrentCondition.wardId,
          }}
          areaList={createDisplayArea(
            modalMasterData.area,
            locationAndStationModalJobCount
          )}
          cityList={createDisplayCity(
            modalMasterData.area,
            locationAndStationModalJobCount
          )}
          onClickSearch={(checkedState) => {
            onClickLocationModalSearchButton({
              areaId: checkedState.checkedAllArea,
              prefectureId: checkedState.checkedAllPref,
              cityId: checkedState.checkedAllCity,
              wardId: checkedState.checkedWard,
            });
          }}
          onChangeSelectedLocation={(checkedState) => {
            updateModalJobCount({
              areaId: checkedState.checkedAllArea,
              prefectureId: checkedState.checkedAllPref,
              cityId: checkedState.checkedAllCity,
              wardId: checkedState.checkedWard,
            });
          }}
          submitButtonText="この条件で検索する"
          validationFlags={{
            isIncomeRangeError: income.isIncomeRangeError,
            isConditionUpperLimitError,
          }}
        />
        <StationModal
          show={stationModal.isOpen}
          onClickClose={stationModal.onClose}
          initialTotalCount={modalTotalCount}
          areaList={createDisplayArea(
            modalMasterData.area,
            locationAndStationModalJobCount
          )}
          railwayCompanyList={createDisplayStation(
            routeMaster,
            locationAndStationModalJobCount
          )}
          onClickSearch={(checkedState) => {
            onClickRoteStationModalSearchButton({
              routeId: checkedState.checkedAllRoute,
              stationId: checkedState.checkedStation,
            });
          }}
          onChangeSelectedStation={(checkedState) => {
            updateModalJobCount({
              routeId: checkedState.checkedAllRoute,
              stationId: checkedState.checkedStation,
            });
          }}
          submitButtonText="この条件で検索する"
          validationFlags={{
            isIncomeRangeError: income.isIncomeRangeError,
            isConditionUpperLimitError,
          }}
          dispatch={checkedStationDispatch}
          checkedState={checkedStationState}
          reloadRouteMaster={reloadRouteMaster}
        />
        <IndustryModal
          show={industryModal.isOpen}
          onClickClose={industryModal.onClose}
          onClickSearch={(checkedState) => {
            onClickIndustryModalSearchButton({
              industryLId: checkedState.L,
              industrySId: checkedState.S,
            });
          }}
          onChangeSelectedIndustry={(checkedState) => {
            updateModalJobCount({
              industryLId: checkedState.L,
              industrySId: checkedState.S,
            });
          }}
          displayIndustryData={createIndustryDisplayData(
            modalMasterData.industry,
            industryModalJobCount
          )}
          matchJobCount={modalTotalCount}
          submitButtonText="この条件で検索する"
          selectedIndustryIdList={{
            L: selectedCurrentCondition.industryLId,
            S: selectedCurrentCondition.industrySId,
          }}
          validationFlags={{
            isIncomeRangeError: income.isIncomeRangeError,
            isConditionUpperLimitError,
          }}
        />
      </ModalOverlay>
    </>
  );
};
